import { useState } from 'react';
import {
  apiErrText, rmb, staffWiApprove, staffWiSupplement,
  COMMERCIAL_ROUTE_LABEL,
  type CommercialRoute, type InstructionDetail,
} from '../lib/api';
import { toast } from './ui/Toast';

/**
 * 商务拍板 — the 2026-07 meeting's whole point: somebody with the client relationship decides, BEFORE the
 * money is promised to 乙方, whether this variation gets billed on to 甲方 or absorbed. So the route radio
 * is required and has no default: a pre-selected 「跟甲方签洽商」 is exactly the unconsidered answer the
 * redesign exists to stop.
 *
 * Two guards ride along:
 *   · `expected_total_rmb` — the total the approver is LOOKING at. If 预算 re-priced in another tab, the
 *     server answers 409 total_changed rather than approving a number nobody read (the 认价 stale-guard).
 *   · lump-sum 计日工 — 本合同无计日工条款. The server refuses approval until a 补充协议 is on file, so the
 *     upload sits right here rather than sending 商务 hunting for it.
 *
 * MONEY SURFACE — never rendered for site_pm.
 */
export default function InstructionApprovalCard({ detail, canApprove, onChanged }: {
  detail: InstructionDetail;
  canApprove: boolean;
  onChanged: () => void;
}) {
  const wi = detail.instruction;
  // Every version comes back on the detail read; the OPEN one is what an approval binds to.
  const pcf = (detail.price_confirmations ?? []).find((p) => p.status === 'open')
    ?? (detail.price_confirmations ?? []).slice(-1)[0] ?? null;
  const [route, setRoute] = useState<CommercialRoute | ''>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The total the button binds to: the open 认价单 if the server opened one, else the sum of the lines.
  const total = detail.totals?.audited_rmb ?? pcf?.total_audited_rmb
    ?? detail.lines.reduce((s, l) => s + (l.audited_amount_rmb ?? 0), 0);
  const declared = detail.totals?.declared_rmb ?? pcf?.total_declared_rmb
    ?? detail.lines.reduce((s, l) => s + (l.declared_amount_rmb ?? 0), 0);

  // G7 — a 固定总价 contract has no 计日工 clause at all; the 补充协议 is what makes the order billable.
  const needsSupplement = wi.kind === 'daywork' && wi.contract_form === 'lump_sum' && !wi.supplement_file_id;

  async function upload(f: File) {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      // The route looks for the part named `supplement` first (routes/workInstructionsMoney.ts).
      fd.append('supplement', f);
      await staffWiSupplement(wi.id, fd);
      toast('补充协议已上传 / supplement uploaded', { kind: 'success' });
      onChanged();
    } catch (e) { setErr(apiErrText(e)); }
    finally { setBusy(false); }
  }

  async function approve() {
    if (!route) { setErr('请选择商务处理方式 / pick a commercial route'); return; }
    setBusy(true); setErr(null);
    try {
      await staffWiApprove(wi.id, { commercial_route: route, note_md: note.trim() || undefined, expected_total_rmb: total });
      toast('已批准，等待劳务认价 / approved — awaiting the contractor\'s 认价', { kind: 'success' });
      onChanged();
    } catch (e) {
      setErr(apiErrText(e));
      // review 9/18: a stale total must refresh the card, or 批准 keeps re-posting the old figure
      if ((e as { body?: { error?: string } })?.body?.error === 'total_changed') onChanged();
    }
    finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ marginBottom: 10, borderColor: 'var(--accent)' }}>
      <div className="row-wrap" style={{ alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <strong>🧭 商务批准 / Commercial approval</strong>
        <span className="small muted">商务拍板：本项跟甲方签洽商，还是自行消化 / bill it on, or absorb it</span>
        <span style={{ flex: 1 }} />
        <span className="small">申报 / declared {rmb(declared)}</span>
        <span className="small">审核合计 / audited <strong>{rmb(total)}</strong></span>
      </div>

      {wi.approved_at ? (
        <div className="small">
          <span className="pill pill-good small">已批准 / approved</span>{' '}
          {wi.approved_by ?? '—'} · {new Date(wi.approved_at).toLocaleString('zh-CN')}
          {wi.commercial_route && <span> · {COMMERCIAL_ROUTE_LABEL[wi.commercial_route]}</span>}
        </div>
      ) : !canApprove ? (
        <div className="small muted">只有商务或总经理可以批准 / only 商务 or the GM may approve.</div>
      ) : wi.state !== 'priced' ? (
        <div className="small muted">该指令尚未完成定价，不能批准 / pricing is not complete yet.</div>
      ) : (
        <>
          {needsSupplement && (
            <div className="small" style={{ background: 'var(--warn-soft)', padding: '8px 10px', borderRadius: 6, marginBottom: 8 }}>
              ⚠ 本合同无计日工条款，需上传补充协议 / this lump-sum contract has no daywork clause — upload the 补充协议 before approving.
              <div style={{ marginTop: 6 }}>
                <input type="file" accept="image/*,application/pdf" disabled={busy}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
              </div>
            </div>
          )}
          {wi.supplement_file_id && <div className="small" style={{ marginBottom: 6 }}><span className="pill pill-good small">✓ 补充协议已上传 / supplement on file</span></div>}

          <div className="field">
            <label>商务处理方式（必选）/ commercial route (required)</label>
            {(['bill_client', 'absorb'] as CommercialRoute[]).map((r) => (
              <label key={r} className="row" style={{ gap: 6, alignItems: 'center', minHeight: 44 }}>
                <input type="radio" name={`route-${wi.id}`} checked={route === r} onChange={() => setRoute(r)} />
                <span className="small">{COMMERCIAL_ROUTE_LABEL[r]}</span>
              </label>
            ))}
          </div>
          <div className="field">
            <label>批准说明 / note（可选 optional）</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} style={{ minHeight: 44 }} />
          </div>

          {err && <div className="small" style={{ color: 'var(--bad)', marginBottom: 6 }}>{err}</div>}
          <button className="btn-primary" style={{ minHeight: 44 }} disabled={busy || !route || needsSupplement} onClick={approve}>
            ✓ 批准 {rmb(total)} / Approve
          </button>
          <div className="small muted" style={{ marginTop: 4 }}>
            批准将按当前显示的金额 {rmb(total)} 提交；若预算同时改价，系统会拒绝并要求刷新。/ the approval binds the amount shown; a concurrent re-price is refused, not silently accepted.
          </div>
        </>
      )}
    </div>
  );
}
